import * as React from "react";
import clsx from "clsx";
import { useI18n } from "../../lib/i18n";
import { ListViewCertificationModuleCard } from "../../../ui/components/ListView/components/ListViewCertificationModuleCard";
import { RewardChipCard } from "../../../ui/components/ListView/components/RewardChipCard";
import type { RewardRule } from "../../lib/api/certification-reward-rules";
import * as s from "./RewardRuleCard.css";

export interface RewardRuleCardProps {
  rule: RewardRule;
  onClick: () => void;
}

export function RewardRuleCard({ rule, onClick }: RewardRuleCardProps) {
  const { t } = useI18n();

  return (
    <div className={s.card} onClick={onClick}>
      <div className={s.header}>
        <h3 className={s.name}>{rule.name}</h3>
        <span
          className={clsx(s.statusPill, rule.enabled ? s.statusPillEnabled : s.statusPillDisabled)}
        >
          {rule.enabled
            ? t("pro.system.rewardRules.enabledBadge")
            : t("pro.system.rewardRules.disabledBadge")}
        </span>
      </div>

      <div className={s.columns}>
        <div className={s.column}>
          <p className={s.columnTitle}>{t("pro.system.rewardRules.requirementsLabel")}</p>
          <div className={s.columnStack}>
            {rule.requirements.length === 0 ? (
              <span className={s.empty}>—</span>
            ) : (
              rule.requirements.map((req) => (
                <ListViewCertificationModuleCard
                  key={req.moduleId}
                  layout="stack"
                  module={{
                    id: req.moduleId,
                    title: req.title,
                    slug: req.slug,
                    heroImageUrl: req.heroImageUrl,
                    isActive: true,
                  }}
                />
              ))
            )}
          </div>
        </div>

        <div className={s.column}>
          <p className={s.columnTitle}>{t("pro.system.rewardRules.rewardsLabel")}</p>
          <div className={s.columnStack}>
            {rule.rewards.roles.length === 0 && rule.rewards.permissions.length === 0 ? (
              <span className={s.empty}>—</span>
            ) : (
              <>
                {rule.rewards.roles.map((role) => (
                  <RewardChipCard
                    key={role.roleId}
                    layout="stack"
                    item={{
                      id: role.roleId,
                      title: role.label || role.name,
                      slug: role.name,
                      kind: "role",
                    }}
                  />
                ))}
                {rule.rewards.permissions.map((perm) => (
                  <RewardChipCard
                    key={perm.permissionId}
                    layout="stack"
                    item={{
                      id: perm.permissionId,
                      title: perm.name,
                      slug: perm.name,
                      kind: "permission",
                    }}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
